import type { FC } from "react";
import { useContext } from "react";

import type { IWidgetModelTypesType } from "app/components/widget/widget-types/Widget";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, timeFilter } from "app/filters/ngFilters";
import { IndustryOverviewContext } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/IndustryOverviewContext";
import { WithIndustryOverviewContextHOC } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/withIndustryOverviewContextHOC";
import { NoData } from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";

import type { RawDataPop } from "../../types";
import Header from "../common/Header";
import Cube from "../key-indicators/components/Cube";
import { initAverageDataPop } from "../utils";

import TotalVisits from "./components/total-visits/TotalVisits";
import {
    KeyIndicatorsPopWrapper,
    NoDataContainer,
    ContentContainer,
    TotalVisitsContainer,
    CubeContainer,
    CubeRow,
} from "./styledComponents";

interface IKeyIndicatorsPop {
    dataPop?: RawDataPop;
    isLoading: boolean;
}

const KeyIndicatorsPop: FC<IKeyIndicatorsPop> = ({ dataPop, isLoading }) => {
    const totalVisitsData = dataPop?.TotalVisits;
    const averageData = initAverageDataPop(dataPop?.Average);
    const { services } = useContext(IndustryOverviewContext);

    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    const getFilteredValue = (num: number) => abbrNumberFilter()(num, 0);

    const selectedCustomIndustryHasSegments = CategoryServiceUtils.selectedCategoryHasSegments();

    return (
        <KeyIndicatorsPopWrapper data-automation="KeyIndicatorsPop">
            <Header
                title={translate("industryoverview.key.indicators.header.title")}
                tooltip={translate("industryoverview.key.indicators.header.title.tooltip")}
                isLoading={isLoading}
                services={services}
                isPop
                addToDashboardProps={{
                    isVisible: !selectedCustomIndustryHasSegments,
                    metric: "EngagementVisits",
                    type: "PeriodOverPeriodVisits" as IWidgetModelTypesType,
                }}
            />
            {!dataPop && !isLoading ? (
                <NoDataContainer>
                    <NoData
                        noDataTitleKey="global.nodata.notavilable"
                        noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                    />
                </NoDataContainer>
            ) : (
                <ContentContainer>
                    <TotalVisitsContainer>
                        <TotalVisits
                            data={totalVisitsData}
                            isLoading={isLoading}
                        />
                    </TotalVisitsContainer>
                    <CubeContainer>
                        <CubeRow>
                            {averageData?.Visits?.Value && !selectedCustomIndustryHasSegments ? (
                                <Cube
                                    title={translate("industryoverview.key.indicators.cube.visits.per.visitor")}
                                    iconName="month"
                                    valNum={getFilteredValue(averageData.VisitsPerVisitor.Value)}
                                    previousValue={getFilteredValue(averageData.VisitsPerVisitor.PreviousPeriodValue)}
                                    change={averageData.VisitsPerVisitor.Change}
                                    tooltip={translate(
                                        "industryoverview.key.indicators.cube.visits.per.visitor.tooltip",
                                    )}
                                    isLoading={isLoading}
                                />
                            ) : null}
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.unbounced.visits")}
                                iconName="bounce-rate-2"
                                valNum={getFilteredValue(averageData.TotalUnbouncedVisits.Value)}
                                previousValue={getFilteredValue(averageData.TotalUnbouncedVisits.PreviousPeriodValue)}
                                change={averageData.TotalUnbouncedVisits.Change}
                                tooltip={translate("industryoverview.key.indicators.cube.unbounced.visits.tooltip")}
                                isLoading={isLoading}
                            />
                        </CubeRow>
                        <CubeRow>
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.pages.per.visit")}
                                iconName="pages-per-visit"
                                valNum={getFilteredValue(averageData.PagesPerVisit.Value)}
                                previousValue={getFilteredValue(averageData.PagesPerVisit.PreviousPeriodValue)}
                                change={averageData.PagesPerVisit.Change}
                                tooltip={translate("industryoverview.key.indicators.cube.pages.per.visit.tooltip")}
                                isLoading={isLoading}
                            />
                            <Cube
                                title={translate("industryoverview.key.indicators.cube.visit.duration")}
                                iconName="avg-visit-duration"
                                valNum={timeFilter()(averageData.VisitDuration.Value, null)}
                                previousValue={timeFilter()(averageData.VisitDuration.PreviousPeriodValue, null)}
                                change={averageData.VisitDuration.Change}
                                tooltip={translate("industryoverview.key.indicators.cube.visit.duration.tooltip")}
                                isLoading={isLoading}
                            />
                        </CubeRow>
                    </CubeContainer>
                </ContentContainer>
            )}
        </KeyIndicatorsPopWrapper>
    );
};

export default WithIndustryOverviewContextHOC(KeyIndicatorsPop);
